'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { UploadProgressToast } from '#pplx/frontend/apps/ask/connectors/components/UploadProgressToast.js';
import { getUploadProgressContext } from '#pplx/frontend/apps/ask/queries/file-queries/uploadProgressContext.js';
import React, { useCallback, useMemo, useState } from 'react';
const UploadProgressContext = getUploadProgressContext();
const isSameFileRepository = (a, b) => {
    return a.file_repository_type === b.file_repository_type && a.owner_id === b.owner_id;
};
const calculateLocalProgress = (localFileUploads) => {
    const pending = localFileUploads.filter((upload) => upload.status === 'uploading').length;
    const errors = localFileUploads.filter((upload) => upload.status === 'failed').length;
    const total = localFileUploads.length;
    return { total, pending, errors };
};
export const UploadProgressProvider = React.memo(function UploadProgressProvider({ children }) {
    const [state, setState] = useState([]);
    const startMonitoring = useCallback(({ fileRepoInfo, collectionSlug, onViewFiles, }) => {
        setState((prev) => {
            // Check if we're already monitoring this repository
            const alreadyMonitoring = prev.some((existingState) => isSameFileRepository(existingState.fileRepoInfo, fileRepoInfo) &&
                existingState.collectionSlug === collectionSlug);
            if (alreadyMonitoring) {
                return prev;
            }
            return [
                ...prev,
                {
                    id: prev.length + 1,
                    fileRepoInfo,
                    collectionSlug,
                    onViewFiles,
                    localFileUploads: [],
                    progressBarVisible: false,
                },
            ];
        });
    }, []);
    const stopMonitoring = useCallback((id) => {
        setState((prev) => {
            return prev.filter((x) => x.id !== id);
        });
    }, []);
    const isMonitoryingFileRepository = useCallback((fileRepoInfo) => {
        return state.some((state) => isSameFileRepository(state.fileRepoInfo, fileRepoInfo));
    }, [state]);
    const setProgressBarVisible = useCallback(({ fileRepoInfo, visible }) => {
        setState((prevStates) => prevStates.map((state) => {
            if (!isSameFileRepository(state.fileRepoInfo, fileRepoInfo)) {
                return state;
            }
            return {
                ...state,
                progressBarVisible: visible,
            };
        }));
    }, []);
    const addLocalFileUpload = useCallback(({ fileRepoInfo, fileId }) => {
        setState((prevStates) => prevStates.map((state) => {
            if (!isSameFileRepository(state.fileRepoInfo, fileRepoInfo)) {
                return state;
            }
            return {
                ...state,
                localFileUploads: [
                    ...state.localFileUploads,
                    { fileId, status: 'uploading' },
                ],
            };
        }));
    }, []);
    const clearLocalFileUploads = useCallback((fileRepoInfo) => {
        setState((prevStates) => prevStates.map((state) => {
            if (!isSameFileRepository(state.fileRepoInfo, fileRepoInfo)) {
                return state;
            }
            return {
                ...state,
                localFileUploads: [],
            };
        }));
    }, []);
    const updateLocalFileUploadStatus = useCallback(({ fileRepoInfo, fileId, status, }) => {
        setState((prevStates) => prevStates.map((state) => {
            if (!isSameFileRepository(state.fileRepoInfo, fileRepoInfo)) {
                return state;
            }
            const updatedUploads = state.localFileUploads.map((upload) => {
                if (upload.fileId === fileId) {
                    return { ...upload, status };
                }
                return upload;
            });
            return {
                ...state,
                localFileUploads: updatedUploads,
            };
        }));
    }, []);
    const removeLocalFileUpload = useCallback(({ fileRepoInfo, fileId }) => {
        setState((prevStates) => prevStates.map((state) => {
            if (!isSameFileRepository(state.fileRepoInfo, fileRepoInfo)) {
                return state;
            }
            const filteredUploads = state.localFileUploads.filter((upload) => upload.fileId !== fileId);
            return {
                ...state,
                localFileUploads: filteredUploads,
            };
        }));
    }, []);
    const getLocalProgress = useCallback((fileRepoInfo) => {
        const matchingState = state.find((existingState) => isSameFileRepository(existingState.fileRepoInfo, fileRepoInfo));
        if (!matchingState) {
            return { total: 0, pending: 0, errors: 0 };
        }
        return calculateLocalProgress(matchingState.localFileUploads);
    }, [state]);
    const contextValue = useMemo(() => ({
        startMonitoring,
        isMonitoryingFileRepository,
        setProgressBarVisible,
        addLocalFileUpload,
        updateLocalFileUploadStatus,
        removeLocalFileUpload,
        clearLocalFileUploads,
        getLocalProgress,
    }), [
        startMonitoring,
        isMonitoryingFileRepository,
        setProgressBarVisible,
        addLocalFileUpload,
        updateLocalFileUploadStatus,
        removeLocalFileUpload,
        clearLocalFileUploads,
        getLocalProgress,
    ]);
    const currentState = state[state.length - 1];
    const showToast = currentState &&
        !currentState.progressBarVisible &&
        currentState.fileRepoInfo.file_repository_type !== 'ORG';
    const onClose = useCallback(() => {
        if (currentState?.id) {
            stopMonitoring(currentState.id);
        }
    }, [currentState?.id, stopMonitoring]);
    return (_jsxs(UploadProgressContext.Provider, { value: contextValue, children: [children, showToast && currentState && (_jsx(UploadProgressToast, { fileRepoInfo: currentState.fileRepoInfo, collectionSlug: currentState.collectionSlug, onViewFiles: currentState.onViewFiles, onClose: onClose }))] }));
});
//# sourceMappingURL=UploadProgressProvider.js.map